import { contentManifest } from "./generated/content";
import type {
  ConversationContent,
  LocalizedConversation,
  LocalizedQuestion,
  LocalizedReflection,
  LocalizedStory,
  QuestionContent,
  ReflectionContent,
  StoryContent,
  UpcomingConversationPreview,
} from "./content-model";
import type { Language } from "./types";

export const conversations: readonly ConversationContent[] =
  contentManifest.conversations;
export const publishedConversationSlugs: readonly string[] =
  contentManifest.publishedSlugs;
export const upcomingConversationPreviews: readonly UpcomingConversationPreview[] =
  contentManifest.upcomingPreviews;
export const stories: readonly StoryContent[] = contentManifest.stories;
export const publishedStorySlugs: readonly string[] =
  contentManifest.publishedStorySlugs;
export const reflections: readonly ReflectionContent[] =
  contentManifest.reflections;
export const publishedReflectionSlugs: readonly string[] =
  contentManifest.publishedReflectionSlugs;
export const questions: readonly QuestionContent[] = contentManifest.questions;
export const publishedQuestionSlugs: readonly string[] =
  contentManifest.publishedQuestionSlugs;

export const featuredConversation = conversations.find(
  (conversation) =>
    conversation.status === "published" && conversation.featured,
);

if (!featuredConversation)
  throw new Error("HeartSpeaks requires one featured published conversation.");

export function getConversation(slug: string): ConversationContent | undefined {
  return conversations.find((conversation) => conversation.slug === slug);
}

export function getConversationLocale(
  conversation: ConversationContent,
  language: Language,
): LocalizedConversation {
  const locale =
    conversation.locales[language] ||
    conversation.locales.en ||
    conversation.locales.ta;
  if (!locale)
    throw new Error(
      `Conversation ${conversation.slug} has no readable locale.`,
    );
  return locale;
}

export function getStoryLocale(
  story: StoryContent,
  language: Language,
): LocalizedStory {
  const locale =
    story.locales[language] || story.locales.en || story.locales.ta;
  if (!locale) throw new Error(`Story ${story.slug} has no readable locale.`);
  return locale;
}

export function getReflectionLocale(
  reflection: ReflectionContent,
  language: Language,
): LocalizedReflection {
  const locale =
    reflection.locales[language] ||
    reflection.locales.en ||
    reflection.locales.ta;
  if (!locale)
    throw new Error(`Reflection ${reflection.slug} has no readable locale.`);
  return locale;
}

const interactionCopy = {
  en: {
    experiencesTitle: "Reader experiences",
    experiencesIntro:
      "Stories shared by readers and published with their permission.",
    empty:
      "The first reader experiences are being carefully reviewed. Yours could help begin this conversation.",
    unavailable:
      "Reader stories are temporarily unavailable. The reflection remains here for you.",
    resonate: "This resonated with me",
    resonated: "Thank you for letting us know.",
    formTitle: "Tell us your experience",
    formIntro:
      "There is no polished answer expected. Begin with what comes to mind.",
    storyLabel: "What is your honest answer?",
    storyPlaceholder: "I’ve been thinking about…",
    identityLabel: "How should we hold your response?",
    consent:
      "I understand that, if selected, this experience may be edited for clarity and published by HeartSpeaks. I can request its removal later.",
    privateHelp:
      "Private reflections are stored for your own participation and will never be published.",
    submit: "Send for careful review",
    success:
      "Your experience is with us. A human editor will review it before anything is published.",
  },
  ta: {
    experiencesTitle: "வாசகர்களின் அனுபவங்கள்",
    experiencesIntro:
      "வாசகர்கள் பகிர்ந்து, அவர்களின் அனுமதியுடன் வெளியிடப்பட்ட கதைகள்.",
    empty:
      "முதல் வாசகர் அனுபவங்கள் கவனமாக பரிசீலிக்கப்படுகின்றன. உங்கள் அனுபவம் இந்த உரையாடலைத் தொடங்க உதவலாம்.",
    unavailable:
      "வாசகர் கதைகள் தற்காலிகமாகக் கிடைக்கவில்லை. இந்தச் சிந்தனை உங்களுக்காக இங்கே உள்ளது.",
    resonate: "இது என் மனதைத் தொட்டது",
    resonated: "எங்களிடம் பகிர்ந்ததற்கு நன்றி.",
    formTitle: "உங்கள் அனுபவத்தைச் சொல்லுங்கள்",
    formIntro:
      "அழகாக எழுதப்பட்ட பதில் தேவையில்லை. மனதில் தோன்றுவதிலிருந்து தொடங்குங்கள்.",
    storyLabel: "உங்களுடைய நேர்மையான பதில் என்ன?",
    storyPlaceholder: "நான் சிந்தித்துக் கொண்டிருப்பது…",
    identityLabel: "உங்கள் பதிலை எவ்வாறு வைத்திருக்க வேண்டும்?",
    consent:
      "தேர்ந்தெடுக்கப்பட்டால், தெளிவுக்காகத் திருத்தப்பட்டு HeartSpeaks-ல் வெளியிடப்படலாம் என்பதைப் புரிந்துகொள்கிறேன். பின்னர் அதை நீக்கக் கோரலாம்.",
    privateHelp: "தனிப்பட்ட சிந்தனைகள் வெளியிடப்படாது.",
    submit: "கவனமான பரிசீலனைக்கு அனுப்பவும்",
    success:
      "உங்கள் அனுபவம் எங்களிடம் உள்ளது. எதையும் வெளியிடுவதற்கு முன் ஒரு மனித ஆசிரியர் அதை பரிசீலிப்பார்.",
  },
} as const;

function toConversationView(content: ConversationContent) {
  const locale = (language: Language) => {
    const editorial = getConversationLocale(content, language);
    return {
      ...editorial,
      tamilTitle: editorial.companionTitle,
      intro: editorial.summary,
      ...interactionCopy[language],
    };
  };
  return {
    id: content.id,
    slug: content.slug,
    status: content.status,
    featured: content.featured,
    topic: content.topic,
    publishedAt: content.publishedAt,
    readingMinutes: content.readingMinutes,
    heroLayout: content.heroLayout,
    heroImage: content.heroImage,
    heroMedia: content.heroMedia,
    socialImage: content.socialImage,
    en: locale("en"),
    ta: locale("ta"),
  };
}

export type ConversationView = ReturnType<typeof toConversationView>;
export const conversationViews = conversations.map(toConversationView);
export const conversation = toConversationView(featuredConversation);
export const composites = {
  en: conversation.en.perspectives,
  ta: conversation.ta.perspectives,
} as const;

export function getConversationView(
  slug: string,
): ConversationView | undefined {
  return conversationViews.find((item) => item.slug === slug);
}

function toStoryView(content: StoryContent) {
  return {
    id: content.id,
    slug: content.slug,
    status: content.status,
    featured: content.featured,
    categoryKey: content.categoryKey,
    storyType: content.storyType,
    publicationBasis: content.publicationBasis,
    relatedConversationId: content.relatedConversationId,
    publishedAt: content.publishedAt,
    readingMinutes: content.readingMinutes,
    heroLayout: content.heroLayout,
    heroImage: content.heroImage,
    heroMedia: content.heroMedia,
    socialImage: content.socialImage,
    en: getStoryLocale(content, "en"),
    ta: getStoryLocale(content, "ta"),
  };
}

export type StoryView = ReturnType<typeof toStoryView>;
export const storyViews = stories.map(toStoryView);

export function getStoryView(slug: string): StoryView | undefined {
  return storyViews.find((item) => item.slug === slug);
}

function toReflectionView(content: ReflectionContent) {
  return {
    id: content.id,
    slug: content.slug,
    status: content.status,
    featured: content.featured,
    sourceType: content.sourceType,
    sourceCreator: content.sourceCreator,
    sourceTitle: content.sourceTitle,
    sourcePublication: content.sourcePublication,
    sourceYear: content.sourceYear,
    sourceUrl: content.sourceUrl,
    rightsBasis: content.rightsBasis,
    excerptUsed: content.excerptUsed,
    series: content.series,
    tags: content.tags,
    publishedAt: content.publishedAt,
    readingMinutes: content.readingMinutes,
    heroLayout: content.heroLayout,
    heroImage: content.heroImage,
    heroMedia: content.heroMedia,
    socialImage: content.socialImage,
    relatedConversationIds: content.relatedConversationIds,
    en: getReflectionLocale(content, "en"),
    ta: getReflectionLocale(content, "ta"),
  };
}

export type ReflectionView = ReturnType<typeof toReflectionView>;
export const reflectionViews = reflections.map(toReflectionView);

export function getReflectionView(slug: string): ReflectionView | undefined {
  return reflectionViews.find((item) => item.slug === slug);
}

export function getQuestionLocale(
  question: QuestionContent,
  language: Language,
): LocalizedQuestion {
  const locale =
    question.locales[language] || question.locales.en || question.locales.ta;
  if (!locale)
    throw new Error("Question " + question.slug + " has no readable locale.");
  return locale;
}

function toQuestionView(content: QuestionContent) {
  return {
    id: content.id,
    slug: content.slug,
    status: content.status,
    featured: content.featured,
    topic: content.topic,
    publishedAt: content.publishedAt,
    source: content.source,
    en: getQuestionLocale(content, "en"),
    ta: getQuestionLocale(content, "ta"),
  };
}

export type QuestionView = ReturnType<typeof toQuestionView>;
export const questionViews = questions.map(toQuestionView);

export function getQuestionView(slug: string): QuestionView | undefined {
  return questionViews.find((item) => item.slug === slug);
}
